import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import type { Alert, PaginatedResponse } from "./types";

export function useAlerts(params?: Record<string, string>) {
  return useQuery<PaginatedResponse<Alert>>({
    queryKey: ["alerts", "global", params],
    queryFn: async () => {
      const { data } = await client.get("/alerts/", { params });
      return data;
    },
  });
}

export function useTenantAlerts(tenantId: string, params?: Record<string, string>) {
  return useQuery<PaginatedResponse<Alert>>({
    queryKey: ["alerts", tenantId, params],
    queryFn: async () => {
      const { data } = await client.get(`/tenants/${tenantId}/alerts/`, { params });
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useUpdateAlert(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ alertId, status }: { alertId: string; status: string }) => {
      const { data } = await client.patch(`/tenants/${tenantId}/alerts/${alertId}/`, { status });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useBulkUpdateAlerts(tenantId?: string) {
  const qc = useQueryClient();
  const url = tenantId ? `/tenants/${tenantId}/alerts/bulk` : "/alerts/bulk";
  return useMutation({
    mutationFn: async (payload: { alert_ids: string[]; status: string }) => {
      const { data } = await client.post(url, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}
